import Breadcrumbs from '@/components/Breadcrumbs';
import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import useRoutes from '@/hooks/useRoutes';
import AppLayout from '@/layouts/AppLayout';
import { ClassData, Course } from '@/types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import AmaSessionsCard from './CourseDetail/Partials/AmaSessionsCard';
import CourseContent from './CourseDetail/Partials/CourseContent';
import CourseHeader from './CourseDetail/Partials/CourseHeader';
import ProgressCard from './CourseDetail/Partials/ProgressCard';
import QuickAccess from './CourseDetail/Partials/QuickAccess';

type Props = {
    course: Course & {
        progress?: {
            total_classes: number;
            completed_classes: number;
            percentage: number;
        };
    };
    last_class_viewed?: ClassData;
    first_class?: { id: number; title: string };
};

const CourseDetailContent: React.FC<Props> = ({
    course,
    last_class_viewed,
    first_class,
}) => {
    const { t } = useTranslation();
    const { route } = useRoutes();

    const breadcrumbItems = [
        {
            label: t('course.sectionDetail.courses'),
            href: route('my-account', { tab: 'courses' }).toString(),
        },
        { label: course.title },
    ];

    return (
        <AppLayout>
            <PageTitle title={course.title} />
            <div className="min-h-screen bg-gray-50">
                <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
                    {/* Breadcrumbs */}
                    <div className="mb-6">
                        <Breadcrumbs items={breadcrumbItems} />
                    </div>

                    {/* Course Header */}
                    <CourseHeader
                        course={course}
                        lastClassViewedId={last_class_viewed?.id}
                        firstClassId={first_class?.id}
                    />

                    {/* Progress Card */}
                    {course.is_enrolled ? (
                        <ProgressCard
                            progressPercentage={course.progress?.percentage || 0}
                        />
                    ) : null}

                    {/* AMA Sessions */}
                    {course.has_ama_series && (
                        <AmaSessionsCard courseSlug={course.slug} />
                    )}

                    {/* Quick Access */}
                    {course.has_quick_access && (
                        <QuickAccess course={course} />
                    )}

                    {/* Course Content */}
                    <CourseContent course={course} />
                </div>
            </div>
        </AppLayout>
    );
};

const CourseDetail: React.FC<Props> = ({
    course,
    last_class_viewed,
    first_class,
}) => {
    return (
        <ThemeWrapper>
            <CourseDetailContent
                course={course}
                last_class_viewed={last_class_viewed}
                first_class={first_class}
            />
        </ThemeWrapper>
    );
};

export default CourseDetail;
